// ── PAYMENTS LEDGER ──────────────────────────────────────────────────────────
// Flat list of every client_payment recorded in Gilda, with expandable
// allocation detail per row. One row per real-world bank deposit
// (wire / check / ACH / Zelle / credit card / other). Each row expands to
// show the underlying payment_allocations — which invoices / hours invoices /
// billing summaries the deposit was split across — and lets the bookkeeper
// click through to the source document.
//
// Why per-document, not per-allocation: a payment is the bank event. The
// bookkeeper reconciles bank events against their statement; allocations
// are how Gilda splits the bank event across owed documents. Showing one
// payment per row matches the reconciliation workflow.
//
// Click-through:
//   - invoice allocations → switch to All Invoices sub-tab + auto-open the
//     invoice via the detail-jump pattern (same hint useDetailJump consumes).
//   - hours_invoice allocations → switch to Designer Hours sub-tab.
//   - billing_summary allocations → switch to Billing History sub-tab.
//
// hours_invoices and billing_summaries aren't loaded into AppContext today,
// so we display the document_id as the label when we don't have a friendlier
// reference handy. Click-through still works at the sub-tab level.

import React, { useMemo, useState } from 'react';
import { useAppContext } from './AppContext';
import { fmtMoney } from '../utils';

var DOC_TYPE_LABEL = {
  invoice:         'Invoice',
  hours_invoice:   'Designer Hours',
  billing_summary: 'Billing Summary',
};

export default function PaymentsLedgerSection(props) {
  var ctx = useAppContext();
  var clientPayments     = ctx.clientPayments     || [];
  var paymentAllocations = ctx.paymentAllocations || [];
  var invoices           = ctx.invoices           || [];
  var clients            = ctx.clients            || [];
  var projects           = ctx.projects           || [];

  var es = useState({});  var expanded = es[0]; var setExpanded = es[1];
  var fs = useState('');  var clientFilter = fs[0]; var setClientFilter = fs[1];
  var ms = useState('');  var methodFilter = ms[0]; var setMethodFilter = ms[1];
  var qs = useState('');  var search = qs[0];        var setSearch = qs[1];

  // ── Derived data ──────────────────────────────────────────────────────────
  var rows = useMemo(function() {
    var sorted = clientPayments.slice().sort(function(a, b) {
      return String(b.paymentDate || '').localeCompare(String(a.paymentDate || ''));
    });
    return sorted.filter(function(pm) {
      if (clientFilter && pm.clientId !== clientFilter) return false;
      if (methodFilter && pm.method   !== methodFilter) return false;
      if (search) {
        var q = search.toLowerCase();
        var blob = [
          pm.bankReference || '',
          pm.reference     || '',
          pm.notes         || '',
          pm.method        || '',
          pm.projectLabel  || '',
          (clients.find(function(c){ return c.id === pm.clientId; }) || {}).name || '',
        ].join(' ').toLowerCase();
        if (blob.indexOf(q) < 0) return false;
      }
      return true;
    });
  }, [clientPayments, clientFilter, methodFilter, search, clients]);

  var distinctMethods = useMemo(function() {
    var s = new Set();
    clientPayments.forEach(function(pm) { if (pm.method) s.add(pm.method); });
    return Array.from(s).sort();
  }, [clientPayments]);

  var summary = useMemo(function() {
    var total = rows.reduce(function(s, pm) { return s + (parseFloat(pm.amountTotal) || 0); }, 0);
    return { count: rows.length, total: total };
  }, [rows]);

  // ── Click-through: jump to source document ────────────────────────────────
  // Uses the same localStorage hint pattern useDetailJump consumes,
  // plus a DOM event for components already mounted.
  function jumpTo(targetTabSlug, sourceId) {
    if (sourceId) {
      try { localStorage.setItem('detail-jump', JSON.stringify({ slug: targetTabSlug, id: sourceId, ts: Date.now() })); }
      catch (_e) { /* localStorage may be disabled */ }
    }
    if (typeof props.onSwitchSubTab === 'function') props.onSwitchSubTab(targetTabSlug);
    if (sourceId && typeof window !== 'undefined' && window.dispatchEvent) {
      window.dispatchEvent(new CustomEvent('detail-jump', { detail: { slug: targetTabSlug, id: sourceId } }));
    }
  }

  function onAllocationClick(alloc) {
    if (alloc.documentType === 'invoice')           jumpTo('all', alloc.documentId);
    else if (alloc.documentType === 'hours_invoice') jumpTo('hours', alloc.documentId);
    else if (alloc.documentType === 'billing_summary') jumpTo('sentpackages', alloc.documentId);
  }

  // ── Helpers ───────────────────────────────────────────────────────────────
  function clientName(id) {
    var c = clients.find(function(x){ return x.id === id; });
    return c ? (c.name || c.id) : (id || '—');
  }
  function projectLabel(label) { return label || '—'; }
  function invoiceRef(id) {
    var inv = invoices.find(function(x){ return x.id === id; });
    return inv ? (inv.invoiceRef || id) : id;
  }
  function allocationLabel(alloc) {
    if (alloc.documentType === 'invoice') {
      var inv = invoices.find(function(x){ return x.id === alloc.documentId; });
      if (inv) return (DOC_TYPE_LABEL.invoice + ' \u00b7 ' + (inv.invoiceRef || alloc.documentId));
      return DOC_TYPE_LABEL.invoice + ' \u00b7 ' + alloc.documentId;
    }
    if (alloc.documentType === 'hours_invoice') {
      return DOC_TYPE_LABEL.hours_invoice + ' \u00b7 ' + alloc.documentId;
    }
    if (alloc.documentType === 'billing_summary') {
      return DOC_TYPE_LABEL.billing_summary + ' \u00b7 ' + alloc.documentId;
    }
    return (alloc.documentType || 'unknown') + ' \u00b7 ' + alloc.documentId;
  }
  function toggleRow(id) {
    setExpanded(function(prev) {
      var n = Object.assign({}, prev);
      if (n[id]) delete n[id]; else n[id] = true;
      return n;
    });
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '20px 18px', maxWidth: 1200 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 14 }}>
        <div style={{ fontWeight: 700, fontSize: 16 }}>Payments Ledger</div>
        <div style={{ fontSize: 11, color: '#888' }}>
          Every client payment recorded in Gilda. Each row is one bank event;
          expand to see the underlying invoice allocations and click through
          to the source document.
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <input
          value={search}
          onChange={function(e) { setSearch(e.target.value); }}
          placeholder="Search reference / notes / method" autoCorrect="off" autoCapitalize="none" spellCheck={false} autoComplete="off"
          style={{ flex: 1, minWidth: 240, padding: '7px 10px', fontSize: 12, border: '0.5px solid #E0E0DC', borderRadius: 5, fontFamily: 'inherit' }} />

        <select value={clientFilter}
          onChange={function(e) { setClientFilter(e.target.value); }}
          style={{ padding: '7px 10px', fontSize: 12, border: '0.5px solid #E0E0DC', borderRadius: 5, fontFamily: 'inherit', background: '#fff' }}>
          <option value="">All clients</option>
          {clients.map(function(c) { return <option key={c.id} value={c.id}>{c.name}</option>; })}
        </select>

        <select value={methodFilter}
          onChange={function(e) { setMethodFilter(e.target.value); }}
          style={{ padding: '7px 10px', fontSize: 12, border: '0.5px solid #E0E0DC', borderRadius: 5, fontFamily: 'inherit', background: '#fff' }}>
          <option value="">All methods</option>
          {distinctMethods.map(function(m) { return <option key={m} value={m}>{m}</option>; })}
        </select>

        <div style={{ fontSize: 11, color: '#666', marginLeft: 8 }}>
          {summary.count + ' payment' + (summary.count === 1 ? '' : 's')}
          {' \u00b7 total '}{fmtMoney(summary.total)}
        </div>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, overflow: 'hidden' }}>
        {rows.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: '#888', fontSize: 12, fontStyle: 'italic' }}>
            No payments recorded yet.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#FAFAF8', borderBottom: '0.5px solid #E0E0DC' }}>
                <th style={th}></th>
                <th style={th}>Date</th>
                <th style={th}>Client</th>
                <th style={th}>Project</th>
                <th style={th}>Method</th>
                <th style={Object.assign({}, th, { textAlign: 'right' })}>Amount</th>
                <th style={th}>Bank ref / Note</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(function(pm) {
                var allocs = paymentAllocations.filter(function(a){ return a.paymentId === pm.id; });
                var isOpen = !!expanded[pm.id];
                return (
                  <React.Fragment key={pm.id}>
                    <tr
                      onClick={function() { toggleRow(pm.id); }}
                      style={{ borderBottom: '0.5px solid #f0f0f0', cursor: 'pointer' }}
                      onMouseEnter={function(e){ e.currentTarget.style.background = '#FAFAF8'; }}
                      onMouseLeave={function(e){ e.currentTarget.style.background = '#FFFFFF'; }}>
                      <td style={Object.assign({}, td, { width: 24, color: '#888' })}>{isOpen ? '\u25BE' : '\u25B8'}</td>
                      <td style={td}>{(pm.paymentDate || '').slice(0, 10)}</td>
                      <td style={Object.assign({}, td, { fontWeight: 500 })}>{clientName(pm.clientId)}</td>
                      <td style={Object.assign({}, td, { color: '#666' })}>{projectLabel(pm.projectLabel)}</td>
                      <td style={td}>{pm.method || '—'}</td>
                      <td style={Object.assign({}, td, { textAlign: 'right', fontFamily: 'monospace' })}>{fmtMoney(pm.amountTotal)}</td>
                      <td style={Object.assign({}, td, { color: '#666', fontSize: 11 })}>
                        {pm.bankReference || pm.reference || pm.notes || ''}
                      </td>
                    </tr>

                    {isOpen && (
                      <tr style={{ background: '#FAFAF7' }}>
                        <td colSpan={8} style={{ padding: '8px 22px 14px 46px' }}>
                          {allocs.length === 0 ? (
                            <div style={{ fontSize: 11, color: '#888', fontStyle: 'italic' }}>
                              No allocations recorded for this payment.
                            </div>
                          ) : (
                            <div>
                              <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888', marginBottom: 6 }}>
                                Allocated to
                              </div>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                                <tbody>
                                  {allocs.map(function(a) {
                                    return (
                                      <tr key={a.id}>
                                        <td style={subTd}>
                                          <button
                                            onClick={function(e) { e.stopPropagation(); onAllocationClick(a); }}
                                            style={{ background: 'none', border: 'none', padding: 0, color: '#1A5C38', cursor: 'pointer', textDecoration: 'underline', fontSize: 11, fontFamily: 'inherit' }}>
                                            {allocationLabel(a)}
                                          </button>
                                        </td>
                                        <td style={Object.assign({}, subTd, { textAlign: 'right', fontFamily: 'monospace', width: 120 })}>
                                          {fmtMoney(a.amount)}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                  <tr>
                                    <td style={Object.assign({}, subTd, { fontWeight: 600, color: '#444' })}>Total</td>
                                    <td style={Object.assign({}, subTd, { textAlign: 'right', fontWeight: 600, fontFamily: 'monospace' })}>
                                      {fmtMoney(allocs.reduce(function(s, a){ return s + (parseFloat(a.amount) || 0); }, 0))}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          )}

                          {pm.notes && (
                            <div style={{ marginTop: 10, padding: '6px 10px', background: '#fff', border: '0.5px solid #E0E0DC', borderRadius: 4, fontSize: 11, color: '#555' }}>
                              <span style={{ color: '#888', fontWeight: 600, marginRight: 6 }}>Notes:</span>
                              {pm.notes}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

var th = { padding: '8px 12px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap' };
var td = { padding: '10px 12px', color: '#444', verticalAlign: 'middle' };
var subTd = { padding: '4px 8px', color: '#444', verticalAlign: 'middle', borderBottom: '0.5px solid #E8E8E4' };
